import { useEffect } from "react";
import { SITE_URL, SITE_NAME } from "@/lib/seo";

const DEFAULTS = {
  title: "",
  description: "",
  canonical: "",
};

function setTag(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLElement>(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

/**
 * Dynamically updates <title>, meta description and canonical URL on the client
 * (e.g. when the user selects an item on a list/detail page). On unmount it
 * restores whatever the route's static head() rendered.
 */
export function useDynamicSeo(input: {
  active: boolean;
  title: string;
  description: string;
  /** Path-only canonical, e.g. "/theater?case=5". SITE_URL is prepended. */
  canonicalPath: string;
}) {
  const { active, title, description, canonicalPath } = input;

  useEffect(() => {
    if (!active) return;
    if (!DEFAULTS.title) {
      DEFAULTS.title = document.title;
      DEFAULTS.description =
        document.head
          .querySelector<HTMLMetaElement>('meta[name="description"]')
          ?.getAttribute("content") ?? "";
      DEFAULTS.canonical =
        document.head
          .querySelector<HTMLLinkElement>('link[rel="canonical"]')
          ?.getAttribute("href") ?? `${SITE_URL}/`;
    }

    const fullTitle = title.includes(SITE_NAME) ? title : `${title} — ${SITE_NAME}`;
    const canonical = canonicalPath.startsWith("http")
      ? canonicalPath
      : `${SITE_URL}${canonicalPath.startsWith("/") ? "" : "/"}${canonicalPath}`;

    document.title = fullTitle;
    setTag('meta[name="description"]', { name: "description", content: description });
    setTag('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    setTag('meta[property="og:description"]', { property: "og:description", content: description });
    setTag('meta[property="og:url"]', { property: "og:url", content: canonical });
    setTag('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    setTag('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    setTag('link[rel="canonical"]', { rel: "canonical", href: canonical });

    return () => {
      // Restore route-level static head when overlay closes / unmounts.
      if (DEFAULTS.title) document.title = DEFAULTS.title;
      setTag('meta[name="description"]', { name: "description", content: DEFAULTS.description });
      setTag('meta[property="og:title"]', { property: "og:title", content: DEFAULTS.title });
      setTag('meta[property="og:description"]', { property: "og:description", content: DEFAULTS.description });
      setTag('meta[property="og:url"]', { property: "og:url", content: DEFAULTS.canonical });
      setTag('meta[name="twitter:title"]', { name: "twitter:title", content: DEFAULTS.title });
      setTag('meta[name="twitter:description"]', { name: "twitter:description", content: DEFAULTS.description });
      setTag('link[rel="canonical"]', { rel: "canonical", href: DEFAULTS.canonical });
    };
  }, [active, title, description, canonicalPath]);
}
